import type { CatalogItem, ReleaseKitSummary } from '../../../../redux/api/fymAPI.types';
import { RELEASE_ARTWORK_PX, releaseArtworkUrl } from '../studio/aiReel/releaseArtworkUrl';

/** One release that the Profile can use: a song with a cover (design b1 "Which release", c3). */
export interface ProfileRelease {
  catalogItemUuid: string;
  title: string;
  artworkUrl: string;
  releaseDate: string | null;
  /** The kit look of the release, or null for a song with no kit. */
  look: ReleaseKitSummary['look'];
  palette: string[];
}

function timeOf(value: string | null): number {
  if (value === null) {
    return Number.NEGATIVE_INFINITY;
  }
  const time = Date.parse(value);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/**
 * The releases with a cover, newest first (design c3 note: "the releases with a cover, newest
 * first"). A song with no release day comes after the dated ones, the newest claim first. Pure.
 */
export function profileReleasesOf(
  items: readonly CatalogItem[] | undefined,
  kits: readonly ReleaseKitSummary[] | undefined
): ProfileRelease[] {
  const looks = new Map((kits ?? []).map((kit) => [kit.catalogItemUuid, kit]));
  return (items ?? [])
    .flatMap((item) => {
      const artworkUrl = releaseArtworkUrl(item, RELEASE_ARTWORK_PX.preview);
      if (artworkUrl === undefined) {
        return [];
      }
      const kit = looks.get(item.uuid);
      return [
        {
          catalogItemUuid: item.uuid,
          title: item.title,
          artworkUrl,
          releaseDate: item.releaseDate,
          look: kit?.look ?? null,
          palette: kit?.palette ?? [],
          claimedAt: item.claimedAt,
        },
      ];
    })
    .sort((a, b) => {
      const byRelease = timeOf(b.releaseDate) - timeOf(a.releaseDate);
      return byRelease !== 0 ? byRelease : timeOf(b.claimedAt) - timeOf(a.claimedAt);
    })
    .map(({ claimedAt: _claimedAt, ...release }) => release);
}
